/**
 * Records which buttons people click, so the layout can follow what is actually used.
 *
 * One capture-phase listener on the document — no component needs changing. The button's
 * name comes from `data-track`, else `aria-label`, else its visible text. Digits in a
 * name become `#` (so "Counter 12" and "Counter 7" are one button) and only the name and
 * screen are sent: never typed text or roster contents. Clicks are batched and sent
 * every FLUSH_MS, and when the tab is hidden or closed.
 */

import { apiClient } from '@/api/client';

export interface ClickEventPayload {
  button: string;
  screen?: string;
  gap_ms?: number;
}

const FLUSH_MS = 30_000;
const MAX_QUEUE = 100;
const NAME_MAX = 60;
const CLICKABLE =
  'button, [role="button"], [role="tab"], [role="switch"], [role="radio"], [role="checkbox"], ' +
  '[role="menuitem"], summary, a[href], [data-track]';
/** Form controls are recorded on `change` (a click on one also opens it or lands on its label). */
const FORM_CONTROL = 'select, input[type="checkbox"], input[type="radio"]';

function clean(raw: string): string {
  return raw.replace(/\s+/g, ' ').replace(/\d+/g, '#').trim().slice(0, NAME_MAX);
}

/** Stable name for a clicked control, or null if it has none worth recording. */
export function describeTarget(target: Element | null): string | null {
  const el = target?.closest?.(CLICKABLE) as HTMLElement | null;
  if (!el) return null;
  // title before text: an icon-only button's text is just the icon.
  const name = clean(
    el.getAttribute('data-track') ||
      el.getAttribute('aria-label') ||
      el.getAttribute('title') ||
      el.textContent ||
      '',
  );
  return name || null;
}

/**
 * Name for a changed dropdown / checkbox / radio, e.g. "select: Break length". A select's
 * own text is all its options, so only its labels are used; a checkbox or radio may also use
 * the text of the label around it. The chosen value is never recorded.
 */
export function describeFormControl(target: Element | null): string | null {
  const el = target?.closest?.(FORM_CONTROL) as HTMLElement | null;
  if (!el) return null;
  const kind = el.tagName === 'SELECT' ? 'select' : (el.getAttribute('type') ?? 'checkbox');
  const label = (el.closest('label') as HTMLElement | null)?.textContent;
  const raw =
    el.getAttribute('data-track') ||
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    el.getAttribute('name') ||
    (kind === 'select' ? '' : label) ||
    '';
  return `${kind}: ${clean(raw) || '(unnamed)'}`;
}

/** "Undo (keyboard)" / "Redo (keyboard)" for Ctrl/Cmd+Z / Y — null for anything else. */
export function describeShortcut(e: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>): string | null {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null;
  const k = e.key.toLowerCase();
  if (k === 'z' && !e.shiftKey) return 'Undo (keyboard)';
  if ((k === 'z' && e.shiftKey) || (k === 'y' && !e.shiftKey)) return 'Redo (keyboard)';
  return null;
}

/** Where the click happened: the route, plus the open dialog's title when inside one. */
export function describeScreen(el: Element | null, pathname: string): string {
  const dialog = el?.closest?.('[role="dialog"]') as HTMLElement | null;
  if (!dialog) return pathname.slice(0, 120);
  const title =
    dialog.getAttribute('aria-label') ||
    dialog.querySelector('h1, h2, h3')?.textContent ||
    'dialog';
  return `${pathname} › ${title.replace(/\s+/g, ' ').replace(/\d+/g, '#').trim()}`.slice(0, 120);
}

let queue: ClickEventPayload[] = [];
let lastClickAt: number | null = null;
let started = false;

function flush(keepalive = false) {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  void apiClient.analytics.sendClicks(batch, keepalive);
}

export function startClickTracking() {
  if (started || typeof document === 'undefined') return;
  started = true;

  const record = (button: string | null, target: Element | null) => {
    if (!button) return;
    const now = Date.now();
    queue.push({
      button,
      screen: describeScreen(target, window.location.pathname),
      gap_ms: lastClickAt === null ? undefined : now - lastClickAt,
    });
    lastClickAt = now;
    if (queue.length >= MAX_QUEUE) flush();
  };
  const asElement = (t: EventTarget | null) => (t instanceof Element ? t : null);
  const opts = { capture: true, passive: true } as const;

  document.addEventListener('click', (e) => {
    const target = asElement(e.target);
    // A checkbox / radio / select is recorded on `change`, not here.
    if (target?.closest?.(FORM_CONTROL)) return;
    record(describeTarget(target), target);
  }, opts);
  document.addEventListener('change', (e) => {
    const target = asElement(e.target);
    record(describeFormControl(target), target);
  }, opts);
  document.addEventListener('keydown', (e) => {
    const t = asElement(e.target) as HTMLElement | null;
    // In a text field Ctrl+Z is the browser's own text undo, not the roster's.
    if (t && (t.isContentEditable || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT'
      || (t.tagName === 'INPUT' && !['checkbox', 'radio', 'button'].includes((t as HTMLInputElement).type)))) return;
    record(describeShortcut(e), t);
  }, opts);

  window.setInterval(() => flush(), FLUSH_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}
